/**
 * BaigLabs parent landing — high-level portfolio, not a single vertical product.
 */
import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  keyframes,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import BaiglabsLogo from '../../components/BaiglabsLogo';
import ErrorBoundary from '../../components/ErrorBoundary';
import { openBaiglabsProduct, productsForSurface } from '../../config/baiglabsProducts';

const FONT_LINK_ID = 'baiglabs-parent-fonts';
const DISPLAY = '"Fraunces", Georgia, serif';
const BODY = '"Source Sans 3", "Source Sans Pro", system-ui, sans-serif';
const WORDMARK = '"Manrope", "Segoe UI", system-ui, sans-serif';
const INK = '#0c1a2e';
const ACCENT = '#1a6bb5';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const markIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;

function ensureFonts() {
  if (typeof document === 'undefined' || document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=Manrope:wght@600;700&family=Source+Sans+3:wght@400;600;700&display=swap';
  document.head.appendChild(link);
}

function LandingContentBaiglabs() {
  const navigate = useNavigate();
  const products = productsForSurface({ signedIn: false });

  useEffect(() => {
    ensureFonts();
  }, []);

  return (
    <ErrorBoundary>
      <Box
        sx={{
          minHeight: '100vh',
          fontFamily: BODY,
          color: INK,
          background:
            'radial-gradient(ellipse 80% 55% at 50% -15%, rgba(26, 107, 181, 0.16), transparent 55%),'
            + 'linear-gradient(180deg, #f3f6fa 0%, #eef2f7 45%, #e6ecf3 100%)',
        }}
      >
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            borderBottom: '1px solid rgba(12, 26, 46, 0.06)',
            bgcolor: 'rgba(243, 246, 250, 0.88)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Container maxWidth="lg" sx={{ py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <BrandLogo size="small" />
            <Stack direction="row" spacing={1.25}>
              <Button
                onClick={() => navigate('/signin')}
                variant="text"
                sx={{
                  fontFamily: BODY,
                  fontWeight: 600,
                  color: INK,
                  textTransform: 'none',
                  px: 1.5,
                }}
              >
                Sign in
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                variant="contained"
                sx={{
                  fontFamily: BODY,
                  fontWeight: 700,
                  bgcolor: ACCENT,
                  textTransform: 'none',
                  boxShadow: 'none',
                  borderRadius: 1.5,
                  px: 2,
                  '&:hover': { bgcolor: '#155a9a', boxShadow: 'none' },
                }}
              >
                Get started
              </Button>
            </Stack>
          </Container>
        </Box>

        <Box
          component="section"
          sx={{
            minHeight: { xs: '72vh', md: '82vh' },
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(900px 420px at 70% 20%, rgba(12, 74, 122, 0.07), transparent 60%),'
                + 'radial-gradient(700px 380px at 20% 80%, rgba(26, 107, 181, 0.08), transparent 55%)',
              pointerEvents: 'none',
            }}
          />
          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: { xs: 7, md: 9 }, textAlign: 'center' }}>
            <Box
              sx={{
                display: 'inline-flex',
                mb: 2.75,
                animation: `${markIn} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both`,
                filter: 'drop-shadow(0 10px 28px rgba(8, 47, 77, 0.18))',
              }}
            >
              <BaiglabsLogo size={72} />
            </Box>
            <Typography
              component="p"
              sx={{
                fontFamily: WORDMARK,
                fontWeight: 700,
                fontSize: { xs: '2.45rem', md: '3.5rem' },
                letterSpacing: '-0.045em',
                lineHeight: 1.02,
                mb: 1.5,
                color: INK,
                animation: `${fadeUp} 0.55s ease 0.06s both`,
              }}
            >
              BaigLabs
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 500,
                fontSize: { xs: '1.25rem', md: '1.55rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.35,
                mb: 1.75,
                color: 'rgba(12, 26, 46, 0.82)',
                maxWidth: 520,
                mx: 'auto',
                animation: `${fadeUp} 0.55s ease 0.12s both`,
              }}
            >
              Products and platforms for learning, work, and everyday tools
            </Typography>
            <Typography
              sx={{
                fontFamily: BODY,
                fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: 440,
                mx: 'auto',
                mb: 4,
                color: 'rgba(12, 26, 46, 0.62)',
                lineHeight: 1.55,
                animation: `${fadeUp} 0.55s ease 0.18s both`,
              }}
            >
              One family of apps — from exam prep and recruiting to knowledge tools and daily companions.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="center"
              sx={{ animation: `${fadeUp} 0.55s ease 0.24s both` }}
            >
              <Button
                href="#products"
                variant="contained"
                size="large"
                sx={{
                  fontFamily: BODY,
                  fontWeight: 700,
                  px: 3.25,
                  py: 1.2,
                  bgcolor: ACCENT,
                  textTransform: 'none',
                  boxShadow: 'none',
                  borderRadius: 1.5,
                  '&:hover': { bgcolor: '#155a9a', boxShadow: 'none' },
                }}
              >
                Explore products
              </Button>
              <Button
                onClick={() => navigate('/signin')}
                variant="outlined"
                size="large"
                sx={{
                  fontFamily: BODY,
                  fontWeight: 600,
                  px: 3.25,
                  py: 1.2,
                  borderColor: 'rgba(12, 26, 46, 0.2)',
                  color: INK,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255,255,255,0.55)',
                  '&:hover': {
                    borderColor: 'rgba(12, 26, 46, 0.35)',
                    bgcolor: 'rgba(255,255,255,0.85)',
                  },
                }}
              >
                Sign in
              </Button>
            </Stack>
          </Container>
        </Box>

        <Box
          id="products"
          component="section"
          sx={{
            py: { xs: 6, md: 8 },
            borderTop: '1px solid rgba(12, 26, 46, 0.07)',
            bgcolor: 'rgba(255, 255, 255, 0.55)',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              component="h2"
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '1.85rem' },
                letterSpacing: '-0.02em',
                mb: 0.75,
              }}
            >
              Products
            </Typography>
            <Typography
              sx={{
                fontFamily: BODY,
                color: 'rgba(12, 26, 46, 0.6)',
                mb: 3.5,
                maxWidth: 480,
              }}
            >
              Open any product below. Studio is the workspace behind BaigLabs chat, forms, and flows.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 1.75,
              }}
            >
              {products.map((product) => (
                <Box
                  key={product.id}
                  component="button"
                  type="button"
                  data-testid={`baiglabs-product-${product.id}`}
                  onClick={() => openBaiglabsProduct(product, navigate)}
                  sx={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    border: '1px solid rgba(12, 26, 46, 0.08)',
                    borderRadius: 2,
                    bgcolor: '#fff',
                    p: 2.5,
                    fontFamily: BODY,
                    color: INK,
                    transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: 'rgba(26, 107, 181, 0.45)',
                      boxShadow: '0 12px 32px rgba(12, 26, 46, 0.06)',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${ACCENT}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: WORDMARK,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      letterSpacing: '-0.02em',
                      mb: 0.65,
                    }}
                  >
                    {product.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.92rem', color: 'rgba(12, 26, 46, 0.62)', lineHeight: 1.45 }}>
                    {product.tagline}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        <Box
          component="footer"
          sx={{
            py: 3.5,
            borderTop: '1px solid rgba(12, 26, 46, 0.07)',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontFamily: BODY, fontSize: '0.875rem', color: 'rgba(12, 26, 46, 0.5)' }}>
            ©
            {' '}
            {new Date().getFullYear()}
            {' '}
            BaigLabs
          </Typography>
        </Box>
      </Box>
    </ErrorBoundary>
  );
}

export default LandingContentBaiglabs;
